import { UserConstants } from "@@types/IUser";
import { useToastContext } from "@components/common/ui/Toast/Context";
import { useFingerprintContext } from "@contexts/fingerprint/FingerprintContext";
import { affiliationLocalStorageKey } from "@hooks/affiliates/affiliate/useAffiliateData";
import { useUser } from "@contexts/user/UserContext";
import pusher from "@services/pusher";
import { queryClient } from "@services/queryClient";
import { useAffiliateStore, useGlobalStore } from "@stores";
import { isMoreThanAnHourAway } from "@utils/utils";
import axios, { isAxiosError } from "axios";
import { BroadcastChannel } from "broadcast-channel";
import Router from "next/router";
import { destroyCookie, parseCookies, setCookie } from "nookies";
import { createContext, useEffect, useState } from "react";
import { api } from "../../services/apiClient";
import {
  AuthContextData,
  AuthProviderProps,
  SigninClientInfo,
  SigninCredentials,
  SigninFromManagerCredentials,
} from "./AuthContext.types";

const noAuthRoutes = [
  "/signup",
  "/reset-password",
  "/reset-password/[token]",
  "/reset-password/success",
  "/recover-password",
  "/recover-password/success",
  "/purchases/[token]",
  "/email-confirmation/[uuid]",
  "/manager-login/[impersonatedBy]/[uuid]/[code]",
  "/certificate/[id]",
  "/affiliate/[uuid]",
  "/terms-of-use",
];

export const AuthContext = createContext({} as AuthContextData);

export function destroyTokenCookies() {
  destroyCookie(undefined, "admin.token", { path: "/" });
  destroyCookie(undefined, "admin.refreshToken", { path: "/" });
  queryClient.clear();
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [loading, setLoading] = useState(false);
  const [isValid, setIsValid] = useState(true);
  const [showBanner, setShowBanner] = useState(false);
  const [isInvitationsManager, setIsInvitationsManager] = useState(false);
  const [isUserFetch, setIsUserFetch] = useState(false);
  const [isAffiliteSuccesOpen, setIsAffiliteSuccesOpen] = useState(false);

  const [isBiometryPending, setIsBiometryPending] = useState(false);
  const [isBiometryInProgress, setIsBiometryInProgress] = useState(false);
  const [isBiometryError, setIsBiometryError] = useState(false);

  const isManagerLogin = useGlobalStore(state => state.isManagerLogin);
  const setIsManagerLogin = useGlobalStore(state => state.setIsManagerLogin);

  const toast = useToastContext();
  const { getFingerprint } = useFingerprintContext();

  const { "admin.token": token } = parseCookies();
  const { user, refetch: refetchUser } = useUser();
  const { createAffiliation } = useAffiliateStore();

  useEffect(() => {
    try {
      const broadcastAuth = new BroadcastChannel("auth", {
        type: "native",
      });

      broadcastAuth.onmessage = async (message: any) => {
        switch (message) {
          case "signOut":
            if (typeof window !== "undefined") {
              try {
                await destroyTokenCookies();
              } catch (err) {
                Router.isReady && Router.push("/");
              }
            }

            Router.isReady && Router.push("/");
            break;
          case "confirmEmail":
            if (typeof window !== "undefined") {
              await becomeSeller("/dashboard", false);
              Router.push("/dashboard");
            }
            break;
          case "signIn":
            if (typeof window !== "undefined") {
              Router.isReady && Router.push("/dashboard");
            }
            break;
          default:
            break;
        }
      };
    } catch {}
  }, []);

  const checkAffiliationRequest = async () => {
    const affiliationUuid = localStorage.getItem(affiliationLocalStorageKey);
    if (!affiliationUuid) return;

    const affiliationRequestDate = localStorage.getItem(affiliationUuid);
    if (!affiliationRequestDate) return;

    if (!user?.isUpdated) return;

    if (!user?.companyUuid) return;

    if (isMoreThanAnHourAway(affiliationRequestDate)) return;
    localStorage.removeItem(affiliationLocalStorageKey);
    localStorage.removeItem(affiliationRequestDate);

    await becomeSeller("/settings");

    createAffiliation(affiliationUuid)
      .then(() => {
        if (Router.pathname.split("/")[1] === "settings") {
          setIsAffiliteSuccesOpen(true);
        } else {
          toast({ type: "success", title: "Você foi afiliado com sucesso!" });
          setIsAffiliteSuccesOpen(false);
        }

        queryClient.invalidateQueries({ queryKey: ["/products"] });
      })
      .catch(error => {
        if (isAxiosError(error)) {
          const alreadyAffiliated = error?.response?.data?.message.includes("já é afiliado desse");
          toast({
            type: alreadyAffiliated ? "success" : "error",
            title: alreadyAffiliated ? "Sucesso" : "Não foi possível se afiliar",
            description: error?.response?.data?.message ?? "Tente novamente mais tarde",
          });
        } else {
          toast({
            type: "error",
            title: "Não foi possível se afiliar",
            description: "Tente novamente mais tarde",
          });
        }
      });
  };

  const pathname = typeof window !== "undefined" ? window.location.pathname : "";

  useEffect(() => {
    if (token) checkAffiliationRequest();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, user]);

  useEffect(() => {
    if (token) {
      queryClient.clear();
      refetchUser();
      setIsValid(true);
      setIsUserFetch(true);
    } else {
      setIsUserFetch(true);
      if (noAuthRoutes.includes(Router.pathname)) {
        return;
      } else {
        Router.push("/");
      }
    }
  }, [token]);

  useEffect(() => {
    if (user && user.biometryStatus) {
      setIsBiometryPending([UserConstants.BiometryStatus.PENDING].includes(user?.biometryStatus));

      setIsBiometryInProgress(
        [
          UserConstants.BiometryStatus.SENT_DOCUMENTS,
          UserConstants.BiometryStatus.MEDIATION,
          UserConstants.BiometryStatus.STORED_DOCUMENTS,
          UserConstants.BiometryStatus.CAPTURE,
        ].includes(user?.biometryStatus)
      );

      setIsBiometryError(
        [UserConstants.BiometryStatus.REFUSED, UserConstants.BiometryStatus.ERROR].includes(user?.biometryStatus)
      );
    }

    if (user) {
      const isInvitationValid = user.permissions.includes("invitations_manager") && !!user.companyUuid;
      setIsInvitationsManager(isInvitationValid);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, token]);

  useEffect(() => {
    if (user && user.companyUuid) {
      const channel = pusher.subscribe(`notification-${user.companyUuid}`);
      channel.bind("pix-paid", (data: any) => {
        toast({ type: "success", title: data.message });
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  async function signInFromManager({ impersonatedBy, uuid, code }: SigninFromManagerCredentials) {
    await api
      .post(
        `/users/manager-login`,
        { impersonatedBy, uuid, code, source: "manager" },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      )
      .then((response: any) => {
        const broadcastAuth = new BroadcastChannel("auth", { type: "native" });
        const { token, refreshToken } = response.data;
        setTokens(token, refreshToken);
        setIsManagerLogin(!!impersonatedBy);
        broadcastAuth.postMessage("signIn");
        Router.push("/dashboard");
      })
      .catch((err: any) => {
        toast({ type: "error", title: "Credenciais inválidas." });
        Router.push("/");
      });
  }

  async function signIn(props: SigninCredentials, clientInfo?: SigninClientInfo) {
    setLoading(true);
    queryClient.clear();

    try {
      const response = await api.post(
        "/users/login",
        {
          email: props.email,
          password: props.password,
          captchaToken: props.captchaToken,
          clientInfo,
          fingerprint: await getFingerprint(),
          source: "app-web",
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      const broadcastAuth = new BroadcastChannel("auth", { type: "native" });
      const { token, refreshToken } = response.data;
      setTokens(token, refreshToken);

      checkAffiliationRequest();

      broadcastAuth.postMessage("signIn");
      setIsManagerLogin(false);
      Router.push("/dashboard");
    } catch (error) {
      //@ts-ignore
      if (error.response?.data.message) {
        //@ts-ignore
        const bannedAccount = !!error.response.data.message.includes("desativada");

        const bannedMessage = `
          <a href="https://help.kirvano.com/pt-BR/articles/8187245-minha-conta-foi-banida-e-agora" class="bannedAccount" target="_blank">
            Saiba mais clicando aqui
          </a>
        `;

        toast({
          type: "error",
          //@ts-ignore
          title: error.response.data.message,
          description: bannedAccount ? bannedMessage : undefined,
          timeout: bannedAccount ? 15000 : undefined,
        });
      } else {
        toast({ type: "error", title: "Ops! Algo deu errado." });
      }
      throw error;
    } finally {
      setLoading(false);
    }
  }

  async function becomeSeller(redirectUrl?: string, shouldShowToast = true) {
    setLoading(true);
    queryClient.clear();

    try {
      const response = await api.put("/users/become-seller");
      const { token, refreshToken } = response.data;
      setTokens(token, refreshToken);
      Router.replace(redirectUrl ?? "/dashboard").then(() => {
        refetchUser();
      });
    } catch (error: any) {
      if (axios.isAxiosError(error)) {
        shouldShowToast &&
          toast({
            type: "error",
            title: "Ops! Algo deu errado.",
            description: error.response?.data.message,
          });
      } else {
        toast({
          type: "error",
          title: "Ops! Algo deu errado.",
          description: "Tente novamente mais tarde.",
        });
      }
    } finally {
      setLoading(false);
    }
  }

  function setTokens(token: string, refreshToken: string) {
    api.defaults.headers["Authorization"] = `Bearer ${token}`;

    setCookie(undefined, "admin.token", token, {
      maxAge: 60 * 60 * 24 * 30, // 30 days
      path: "/", // What path will have access to the cookie. In this case, all of them.
    });

    setCookie(undefined, "admin.refreshToken", refreshToken, {
      maxAge: 60 * 60 * 24 * 30,
      path: "/",
    });
  }

  function signOut() {
    try {
      const broadcastAuth = new BroadcastChannel("auth", {
        type: "native",
      });
      broadcastAuth.postMessage("signOut");
    } catch {
      destroyTokenCookies();
    }

    Router.push("/");
  }

  return (
    <AuthContext.Provider
      value={{
        signIn,
        signOut,
        isValid,
        isInvitationsManager,
        loading,
        showBanner,
        setTokens,
        setIsValid,
        becomeSeller,
        signInFromManager,
        setShowBanner,
        isBiometryError,
        isBiometryInProgress,
        isBiometryPending,
        isAffiliteSuccesOpen,
        setIsAffiliteSuccesOpen,
        checkAffiliationRequest,
        isManagerLogin,
      }}
    >
      {isUserFetch ? children : ""}
    </AuthContext.Provider>
  );
}
